import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Th,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useCallback, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, typeguards } from '@finley-technologies/finley-types';

import * as DragAndDropTableTypes from '../../../../../../common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from '../../../../../../common-frontend/components/DragAndDropTable/ui';
import {
  DeleteIconButton,
  EditIconButton,
} from '../../../../../../common-frontend/components/IconButton';
import { CreditAgreementFullArtifactWipConfigType } from '../../../../types';
import { getIterationKey } from '../../../common';
import { ReportingOutputDocxUpdateColumnConfigForm } from './ReportingOutputDocxUpdateColumnConfigForm';

type ReportingOutputDocxColumnConfigProps = {
  basePropertyKey: string;
  columnConfig: CreditAgreement.Config.Reporting.Outputs.Docx.ColumnConfig;
  columnPropertyNames?: string[];
  id: string;
  idx: number;
  moveRow: DragAndDropTableTypes.MoveRow;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};
export const ReportingOutputDocxColumnConfig = ({
  basePropertyKey,
  columnConfig,
  columnPropertyNames,
  id,
  idx,
  moveRow,
  wipConfig,
}: ReportingOutputDocxColumnConfigProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const deleteColumnConfig = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const columnConfigs: CreditAgreement.Config.Reporting.Outputs.Docx.ColumnConfig[] = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );

    finleyAssert(
      typeguards.creditAgreement.isDocxColumnConfigs(columnConfigs),
      `Retrieved non-DocxColumnConfigs structure for [key:${basePropertyKey}]`,
    );

    const configsAfterDelete = columnConfigs.filter(
      // we sometimes filter items out based on index to delete from state, but
      // this isn't a reliable method for this particular ui. users can reorder
      // the items and may not have yet saved the new order
      (config) => id !== getIterationKey(config),
    );

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: configsAfterDelete,
      setIsSaveLoading: setIsDeleting,
    });
  }, [wipConfig, basePropertyKey, id]);

  return (
    <TrDraggableHandle
      acceptedType="someType"
      key={`${id}-property-row`}
      id={`${id}-property-row`}
      moveRow={moveRow}
      index={idx}
      isDragDisabled={!wipConfig?.isMutateAllowed(basePropertyKey)}
    >
      <Th>{columnConfig.order}</Th>
      <Th textTransform="none">{columnConfig.name}</Th>
      <Th textTransform="none">{columnConfig.propertyName}</Th>
      <Th>{columnConfig.type}</Th>
      <Th textTransform="none">{columnConfig.format}</Th>
      {wipConfig?.isMutateAllowed(basePropertyKey) && (
        <>
          <Th>
            <EditIconButton
              ariaLabel="edit config"
              label="Edit Config"
              onClick={() => {
                setIsEditOpen(true);
              }}
            />
            <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}>
              <ModalOverlay />
              <ModalContent padding="20px">
                <ModalHeader>Update Column Config</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <ReportingOutputDocxUpdateColumnConfigForm
                    basePropertyKey={basePropertyKey}
                    columnPropertyNames={columnPropertyNames}
                    idx={idx}
                    isLoading={isUpdating}
                    initialValues={{
                      initialColumnName: columnConfig.name,
                      initialColumnPropertyName: columnConfig.propertyName,
                      initialType: columnConfig.type,
                      initialFormat: columnConfig.format,
                    }}
                    onClose={() => setIsEditOpen(false)}
                    setIsLoading={setIsUpdating}
                    wipConfig={wipConfig}
                  />
                </ModalBody>
              </ModalContent>
            </Modal>
          </Th>
          <Th>
            <DeleteIconButton
              ariaLabel="delete config"
              label="Delete Config"
              onClick={async () => {
                setIsDeleting(true);
                await deleteColumnConfig();
              }}
              isLoading={isDeleting}
            />
          </Th>
        </>
      )}
    </TrDraggableHandle>
  );
};
